
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { postDriversRetransmit } from '@/api';
import DateSelect from '@/components/dashBoard/selectsFields/components/DateSelect/DateSelect.vue';
import StateSelect from '@/components/dashBoard/selectsFields/components/StateSelect/StateSelect.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import DatePicker from '@/views/Location/DatePicker.vue';
import { User } from '@/models/main';

import { isValid } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { notificationErrorText, timeZoneDefault } from '@/utils/PortalConstants';
import { fetchDriverRetransmit } from '@/helpers/fetchers';
import * as routeNames from '@/router/routeNames';
import _isEqual from 'lodash/isEqual';

@Component({
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
  components: {
    Button,
    BackButton,
    DatePicker,
    DateSelect,
    StateSelect,
  },
})
export default class DriverRetransmit extends Vue {
  private isLoading = false;
  private user!: User;

  public date = '';

  private driversList = {};
  private checkedList = {};
  private checkedDateList = {};
  private expandedPanels = [0];

  private getDriversList() {
    this.isLoading = true;

    fetchDriverRetransmit(this.date)
      .then(res => {
        if (res && res.length === 0) {
          this.$notify({
            group: 'network',
            title: 'No allocated drivers found',
            text: 'No allocated drivers found for the selected date',
            type: 'warn',
          });
          return;
        }
        this.driversList = { [this.date]: res };
        this.checkedList = {};
        Object.keys(this.driversList).map(item => {
          this.checkedDateList[item] = false;
        });
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error getting driver list',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.isLoading = false;
        // Reset expandedPanels to keep the first panel expanded
        this.expandedPanels = [0];
      });
  }

  public async changeDate(value: string): Promise<void> {
    this.date = value;
  }

  private checkboxClick(idx, id): void {
    // The number 4 here is because we update the state of the original object with checkbox status
    // which means that the object will have original+1 elements
    if (this.driversList[idx][id].length >= 4) {
      if (!this.checkedList[idx]) {
        this.checkedList[idx] = [];
      }
      if (!this.checkedList[idx].includes(id)) {
        this.checkedList[idx].push(id);
      }
    } else {
      this.checkedDateList[idx] = false;
      this.checkedList[idx].splice(this.checkedList[idx].indexOf(id), 1);
      if (this.checkedList[idx].length === 0) {
        delete this.checkedList[idx];
      }
    }
    if (this.checkedList[idx] && this.checkedList[idx].length === this.driversList[idx].length) {
      this.checkedDateList[idx] = true;
    }
  }

  private checkboxDateClick(event, idx): void {
    this.checkedDateList[idx] = true;
    if (!this.checkedList[idx]) {
      this.checkedList[idx] = [];
      this.driversList[idx].map((item, i) => {
        if (item.indexOf('checked') === -1) {
          item.push('checked');
          this.checkedList[idx].push(i);
        }
      });
    } else if (this.checkedList[idx].length === this.driversList[idx].length) {
      this.driversList[idx].map(item => {
        item.splice(item.indexOf('checked'), 1);
      });
      delete this.checkedList[idx];
    } else {
      this.driversList[idx].map((item, i) => {
        if (item.indexOf('checked') === -1) {
          item.push('checked');
          this.checkedList[idx].push(i);
        }
      });
    }
  }

  private retransmitDrivers(): void {
    this.isLoading = true;
    const sendingList: [string, number][] = [];
    Object.keys(this.driversList).map((date: string) => {
      this.driversList[date].map((driver: [number]) => {
        if (driver && driver.length >= 4) {
          sendingList.push([date, driver[0]]);
        }
      });
    });

    for (const [date, driver] of sendingList) {
      postDriversRetransmit(date, driver)
        .then(() => {
          this.$notify({
            group: 'network',
            title: 'Driver retransmitted',
            text: `Driver ${driver} retransmitted successfully`,
            type: 'success',
          });
          this.driversList[date].splice(this.driversList[date].indexOf(driver), 1);
        })
        .catch(() => {
          this.$notify({
            group: 'network',
            title: 'Error retransmitting driver',
            text: notificationErrorText,
            type: 'warn',
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }

  public clickSearch(): void {
    const newRoute = {
      date: this.date,
    };
    const isEqual = _isEqual(newRoute, this.$route.query);
    if (!isEqual) {
      this.$router.push({
        path: this.$route.path,
        query: { ...this.$route.query, ...newRoute },
      });
    } else {
      this.getDriversList();
    }
  }

  @Watch('$route.query', { immediate: true })
  private async routeChanged(): Promise<void> {
    if (this.$route.name === routeNames.DRIVER_RETRANSMIT) {
      // Date: get from the query if valid or set today
      if (this.$route.query?.date && isValid(new Date(String(this.$route.query.date)))) {
        this.date = String(this.$route.query.date);
      } else {
        const userTimeZone = this.user.timezone || timeZoneDefault;
        this.date = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd');
      }
    }
    if (this.$route.query?.date) {
      this.getDriversList();
    }
  }
}
